import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { toast } from 'react-hot-toast'
import { useGetTopics } from '@/hooks/api/dashboard'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { maxPrintsForTier } from '@/lib/featureKeys'
import { PrivacyType, TopicType } from '@/utils/constants'
import { Topic } from '@/types'
import { topicCoverUrl, topicToAlbumSummary } from '@/features/album/mappers'
import { SourceAlbumRef } from '@/features/gallery-studio/hooks/useGallerySourceAlbum'
import { StudioArt, StudioAssetPhoto, StudioPanelTab, StudioPin } from '../../types'
import { STUDIO_COLORS } from '../../constants'
import { GlassPanel, monoLabelSx, STUDIO_ACCENT } from '../shared'

export interface StudioLayersPanelProps {
  arts: StudioArt[]
  selectedId: string | null
  hiddenIds: Set<string>
  onSelectArt: (id: string) => void
  onDoubleClickArt: (id: string) => void
  onToggleVisible: (id: string) => void
  onSelectRoom: () => void
  onHangClick: () => void
  pins: StudioPin[]
  /** Open pin compose/edit; also selects the bound print when present. */
  onSelectPin?: (pin: StudioPin) => void
  assetPhotos: StudioAssetPhoto[]
  assetsLoading: boolean
  hasRealPhotos: boolean
  /** When true, palette is rebuilt from hung wall prints because no source album is linked. */
  assetsFromWallOnly?: boolean
  linkedAlbums: SourceAlbumRef[]
  onAddSourceAlbum: (albumId: string) => void
  onRemoveSourceAlbum: (albumId: string) => void
  onPickAsset: (photo: StudioAssetPhoto) => void
  /** Upload files into a linked album (panel picks target: filter chip or primary). */
  onUploadPhotos?: (albumId: string, files: File[]) => void
  uploadingPhotos?: boolean
  uploadProgress?: number | null
}

const Root = styled(GlassPanel)({
  top: 58,
  left: 12,
  bottom: 78,
  width: 244,
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
})

const PTabs = styled(Box)({ display: 'flex', gap: 2, padding: '8px 8px 0' })
const PTab = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  flex: 1,
  height: 27,
  borderRadius: 8,
  fontSize: 11.5,
  fontWeight: 620,
  textAlign: 'center',
  cursor: 'pointer',
  color: $on ? 'var(--ds-night-ink)' : 'var(--ds-night-ink-subtle)',
  background: $on ? 'rgba(244,243,246,0.08)' : 'transparent',
}))

const Scroll = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '6px 8px 12px',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(244,243,246,0.14) transparent',
})

const SectionLabel = styled(Box)({
  ...monoLabelSx,
  padding: '12px 6px 4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const SectionAction = styled('span')({
  cursor: 'pointer',
  opacity: 0.7,
  fontSize: 9,
  letterSpacing: '0.05em',
  '&:hover': { opacity: 1, color: '#be9dfd' },
})

const PrintCountBadge = styled('span', { shouldForwardProp: (p) => p !== '$atCap' })<{ $atCap?: boolean }>(
  ({ $atCap }) => ({
    marginLeft: 6,
    fontSize: 9,
    letterSpacing: '0.04em',
    opacity: $atCap ? 1 : 0.55,
    color: $atCap ? '#ffcf7d' : 'inherit',
  }),
)

const Row = styled(Box, { shouldForwardProp: (p) => p !== '$sel' })<{ $sel?: boolean }>(({ $sel }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 30,
  padding: '0 7px',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 140ms ease',
  background: $sel ? 'rgba(190,157,253,0.16)' : 'transparent',
  boxShadow: $sel ? 'inset 0 0 0 1px rgba(190,157,253,0.35)' : 'none',
  '&:hover': { background: $sel ? 'rgba(190,157,253,0.16)' : 'rgba(244,243,246,0.06)' },
  '&:hover .row-meta': { display: 'none' },
  '&:hover .row-eye': { display: 'flex' },
}))

const Thumb = styled('img')({ width: 24, height: 17, borderRadius: 4, objectFit: 'cover', background: '#2a2436', flexShrink: 0 })
const RowIcon = styled(Box)({ width: 24, textAlign: 'center', fontSize: 11, opacity: 0.7, flexShrink: 0 })
const RowName = styled(Typography)({
  fontSize: 12,
  fontWeight: 550,
  color: 'var(--ds-night-ink)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})
const RowMeta = styled(Box)({
  ...monoLabelSx,
  marginLeft: 'auto',
  fontSize: 7.5,
  flexShrink: 0,
})
const RowEye = styled(Box, { shouldForwardProp: (p) => p !== '$off' })<{ $off?: boolean }>(({ $off }) => ({
  marginLeft: 'auto',
  width: 22,
  height: 22,
  borderRadius: 6,
  display: $off ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  color: 'var(--ds-night-ink-muted)',
  opacity: $off ? 0.45 : 1,
  flexShrink: 0,
  '&:hover': { background: 'rgba(244,243,246,0.1)' },
}))

const AssetsHint = styled(Box)({
  padding: '2px 4px 8px',
  fontSize: 10.5,
  lineHeight: 1.5,
  color: 'var(--ds-night-ink-subtle)',
})

const AlbumChip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 9px',
  borderRadius: 10,
  background: 'rgba(244,243,246,0.04)',
  border: '1px solid var(--ds-night-border)',
  marginBottom: 6,
})

const ChipThumb = styled(Box)({
  width: 32,
  height: 26,
  borderRadius: 6,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundColor: '#2a2436',
  flexShrink: 0,
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
})

const ChipRemove = styled('button')({
  all: 'unset',
  cursor: 'pointer',
  marginLeft: 'auto',
  width: 18,
  height: 18,
  borderRadius: 5,
  display: 'grid',
  placeItems: 'center',
  fontSize: 11,
  color: 'var(--ds-night-ink-subtle)',
  flexShrink: 0,
  '&:hover': { color: STUDIO_ACCENT.coral, background: 'rgba(254,166,125,0.12)' },
})

/** Quiet text actions under album chips. */
const LinkAlbumLink = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  fontSize: 11.5,
  fontWeight: 600,
  color: STUDIO_ACCENT.violet,
  padding: '2px 2px 10px',
  transition: 'opacity 120ms ease',
  '&:hover': { opacity: 0.85 },
})

/** First cell in the Assets grid — click to upload into the active album. */
const UploadTile = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  position: 'relative',
  borderRadius: 9,
  overflow: 'hidden',
  aspectRatio: '4/3',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  border: `1px dashed rgba(190,157,253,0.45)`,
  background: 'rgba(190,157,253,0.06)',
  color: STUDIO_ACCENT.violet,
  transition: 'border-color 140ms ease, background 140ms ease, transform 140ms ease',
  '&:hover:not(:disabled)': {
    borderColor: 'rgba(190,157,253,0.75)',
    background: 'rgba(190,157,253,0.12)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': { opacity: 0.4, cursor: 'default', transform: 'none' },
})

const UploadTilePlus = styled('span')({
  fontSize: 22,
  fontWeight: 300,
  lineHeight: 1,
  color: STUDIO_ACCENT.violet,
})

const UploadTileLabel = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7.5,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: STUDIO_ACCENT.violet,
})

const PickerRow = styled('button', { shouldForwardProp: (p) => p !== '$disabled' })<{ $disabled?: boolean }>(
  ({ $disabled }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 8px',
    borderRadius: 8,
    cursor: $disabled ? 'default' : 'pointer',
    opacity: $disabled ? 0.45 : 1,
    border: '1px solid transparent',
    '&:hover': $disabled
      ? undefined
      : { background: 'rgba(190,157,253,0.12)', borderColor: 'rgba(190,157,253,0.3)' },
  }),
)

const AssetGrid = styled(Box)({ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, padding: '6px 2px 10px' })
const AssetTile = styled(Box)({
  position: 'relative',
  borderRadius: 9,
  overflow: 'hidden',
  aspectRatio: '4/3',
  cursor: 'pointer',
  border: '1px solid var(--ds-night-border)',
  transition: 'all 140ms ease',
  '&:hover': { borderColor: 'rgba(190,157,253,0.6)', transform: 'translateY(-1px)' },
  '& img': { width: '100%', height: '100%', objectFit: 'cover' },
})

const wallLabel = (side: -1 | 0 | 1) => (side === -1 ? 'L' : side === 1 ? 'R' : 'B')

const PIN_TONE_COLOR: Record<StudioPin['tone'], string> = {
  gold: STUDIO_COLORS.gold,
  violet: STUDIO_COLORS.violet,
  cyan: STUDIO_COLORS.cyan,
}

/** Left panel — Layers (room/lighting/prints) and Assets (linked album palette). */
export function StudioLayersPanel({
  arts,
  selectedId,
  hiddenIds,
  onSelectArt,
  onDoubleClickArt,
  onToggleVisible,
  onSelectRoom,
  onHangClick,
  pins,
  onSelectPin,
  assetPhotos,
  assetsLoading,
  hasRealPhotos,
  assetsFromWallOnly,
  linkedAlbums,
  onAddSourceAlbum,
  onRemoveSourceAlbum,
  onPickAsset,
  onUploadPhotos,
  uploadingPhotos,
  uploadProgress,
}: StudioLayersPanelProps) {
  const [tab, setTab] = useState<StudioPanelTab>('layers')
  const [picking, setPicking] = useState(false)
  const [filterAlbumId, setFilterAlbumId] = useState<string | 'all'>('all')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const getTopics = useGetTopics()
  const { isFeatureAvailable, showUpgrade, tier } = useFeatureGate()
  const printCap = maxPrintsForTier(tier)
  const livePrintCount = useMemo(() => arts.filter((a) => !a.isPlaceholder).length, [arts])
  const atPrintCap = livePrintCount >= printCap
  const printCapLabel = Number.isFinite(printCap) ? `${livePrintCount}/${printCap}` : `${livePrintCount}`

  const onHangClickGated = () => {
    if (atPrintCap) {
      showUpgrade({
        featureKey: FEATURE_KEYS.STUDIO_PRINT_QUOTA,
        headline: `Room is full at ${printCap} prints — upgrade to hang more`,
        upgradeTo: tier === 'pro' ? 'Studio' : 'Gallery',
      })
      return
    }
    onHangClick()
  }

  useEffect(() => {
    if (!picking) return
    getTopics.mutate({
      pageNo: 1,
      interaction: Number(PrivacyType.PRIVATE),
      topicType: Number(TopicType.ALBUM),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picking])

  const linkedIds = useMemo(() => new Set(linkedAlbums.map((a) => a.id)), [linkedAlbums])

  const pickerAlbums = useMemo(() => {
    const topics = (getTopics.data || []) as Topic[]
    return topics
      .filter((t) => String(t.topicType) === String(TopicType.ALBUM))
      .map((t) => {
        const summary = topicToAlbumSummary(t)
        return {
          id: t.topicID,
          title: summary.title,
          coverUrl: topicCoverUrl(t) || summary.coverUrl,
          photoCount: summary.photoCount,
          linked: linkedIds.has(t.topicID),
        }
      })
  }, [getTopics.data, linkedIds])

  const visiblePhotos = useMemo(() => {
    if (filterAlbumId === 'all') return assetPhotos
    return assetPhotos.filter((p) => p.albumId === filterAlbumId || (!p.albumId && filterAlbumId === 'wall'))
  }, [assetPhotos, filterAlbumId])

  const openPicker = () => {
    // First album is always free; 2+ is a Gallery+ perk. Only reachable at
    // all when linkedAlbums.length === 0 now (link-another is deferred to
    // phase 2, see the hidden `LinkAlbumLink` below) — this PRO check stays
    // wired so it's already correct the moment that button comes back.
    // Already-linked albums from before either gate existed are untouched.
    if (linkedAlbums.length >= 1 && !isFeatureAvailable(FEATURE_KEYS.MULTI_SOURCE_ALBUMS)) {
      showUpgrade({
        featureKey: FEATURE_KEYS.MULTI_SOURCE_ALBUMS,
        headline: 'Multi-album Studio is a Gallery+ perk',
      })
      return
    }
    setPicking(true)
  }

  const selectAlbum = (albumId: string, alreadyLinked: boolean) => {
    if (alreadyLinked) {
      toast('Already linked', { icon: '◈' })
      return
    }
    onAddSourceAlbum(albumId)
    setPicking(false)
    setFilterAlbumId(albumId)
    toast.success('Album linked to Assets', { duration: 2200 })
  }

  const uploadTargetAlbumId = useMemo(() => {
    if (linkedAlbums.length === 0) return null
    if (filterAlbumId !== 'all' && linkedAlbums.some((a) => a.id === filterAlbumId)) return filterAlbumId
    return linkedAlbums[0].id
  }, [linkedAlbums, filterAlbumId])

  const openUpload = () => {
    if (!onUploadPhotos) return
    if (!uploadTargetAlbumId) {
      toast('Link an album first — then upload into it', { icon: '◈' })
      return
    }
    fileInputRef.current?.click()
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length || !uploadTargetAlbumId || !onUploadPhotos) return
    onUploadPhotos(uploadTargetAlbumId, files)
  }

  const uploadTargetTitle =
    linkedAlbums.find((a) => a.id === uploadTargetAlbumId)?.title || 'album'

  return (
    <Root>
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*,.heic,.heif'
        multiple
        hidden
        onChange={onFileChange}
      />
      <PTabs>
        <PTab $on={tab === 'layers'} onClick={() => setTab('layers')}>
          Layers
        </PTab>
        <PTab
          $on={tab === 'assets'}
          onClick={() => {
            setTab('assets')
            setPicking(false)
          }}
        >
          Assets
        </PTab>
      </PTabs>

      {tab === 'layers' ? (
        <Scroll>
          <SectionLabel>ROOM</SectionLabel>
          <Row onClick={onSelectRoom}>
            <RowIcon>▦</RowIcon>
            <RowName>Main Room</RowName>
          </Row>
          <Row onClick={onSelectRoom}>
            <RowIcon>☀</RowIcon>
            <RowName>Lighting rig</RowName>
          </Row>

          <SectionLabel>
            <span>
              PRINTS <PrintCountBadge $atCap={atPrintCap}>{printCapLabel}</PrintCountBadge>
            </span>
            <SectionAction onClick={onHangClickGated}>+ HANG</SectionAction>
          </SectionLabel>
          {arts.map((art) => {
            const hidden = hiddenIds.has(art.id)
            return (
              <Row key={art.id} $sel={art.id === selectedId} onClick={() => onSelectArt(art.id)} onDoubleClick={() => onDoubleClickArt(art.id)}>
                <Thumb src={art.img} alt='' />
                <RowName>{art.name}</RowName>
                <RowMeta className='row-meta'>
                  {wallLabel(art.side)} · {art.w.toFixed(1)}M
                </RowMeta>
                <RowEye
                  className='row-eye'
                  $off={hidden}
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleVisible(art.id)
                  }}
                >
                  {hidden ? '◡' : '◉'}
                </RowEye>
              </Row>
            )
          })}

          {pins.length > 0 && (
            <>
              <SectionLabel>STORY</SectionLabel>
              {pins.map((pin) => {
                const print = pin.artId ? arts.find((a) => a.id === pin.artId) : undefined
                const meta = print?.name || (pin.text.trim() ? pin.text.slice(0, 18) : 'Note')
                return (
                  <Row
                    key={pin.id}
                    onClick={() => onSelectPin?.(pin)}
                    sx={onSelectPin ? { cursor: 'pointer' } : undefined}
                  >
                    <RowIcon sx={{ color: PIN_TONE_COLOR[pin.tone] }}>◈</RowIcon>
                    <RowName>{pin.authorName} — pinned note</RowName>
                    <RowMeta className='row-meta'>{meta.toUpperCase()}</RowMeta>
                  </Row>
                )
              })}
            </>
          )}
        </Scroll>
      ) : picking ? (
        <Scroll>
          <SectionLabel>
            ADD ALBUM
            <SectionAction onClick={() => setPicking(false)}>← BACK</SectionAction>
          </SectionLabel>
          <AssetsHint>Pick an album to merge into this gallery’s Assets palette.</AssetsHint>
          {getTopics.isPending && (
            <Typography sx={{ fontSize: 11, color: 'var(--ds-night-ink-subtle)', px: 0.5, py: 1 }}>Loading albums…</Typography>
          )}
          {!getTopics.isPending && pickerAlbums.length === 0 && (
            <Typography sx={{ fontSize: 11, color: 'var(--ds-night-ink-subtle)', px: 0.5, py: 1 }}>
              No albums yet — create one from Albums first.
            </Typography>
          )}
          {pickerAlbums.map((a) => (
            <PickerRow key={a.id} type='button' $disabled={a.linked} onClick={() => selectAlbum(a.id, a.linked)}>
              <ChipThumb
                sx={{
                  backgroundImage: a.coverUrl
                    ? `url(${a.coverUrl})`
                    : 'linear-gradient(145deg, rgba(190,157,253,0.3), rgba(1,218,254,0.15))',
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <RowName>{a.title}</RowName>
                <Typography sx={{ ...monoLabelSx, fontSize: 7, mt: '2px' }}>
                  {a.linked ? 'LINKED' : `${a.photoCount} PHOTOS`}
                </Typography>
              </Box>
            </PickerRow>
          ))}
        </Scroll>
      ) : (
        <Scroll>
          <SectionLabel>ALBUMS</SectionLabel>

          {linkedAlbums.length === 0 && (
            <AssetsHint>
              {assetsFromWallOnly
                ? 'Showing prints already on the wall. Link an album for the full palette.'
                : 'Link an album — its photos become your hang/swap palette.'}
            </AssetsHint>
          )}

          {linkedAlbums.map((a, i) => (
            <AlbumChip key={a.id}>
              <ChipThumb
                sx={{
                  backgroundImage: a.coverUrl
                    ? `url(${a.coverUrl})`
                    : 'linear-gradient(145deg, rgba(190,157,253,0.3), rgba(1,218,254,0.15))',
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <RowName sx={{ fontSize: 11.5 }}>{a.title}</RowName>
                <Typography sx={{ ...monoLabelSx, fontSize: 7, mt: '2px' }}>
                  {i === 0 ? 'PRIMARY · ' : ''}
                  {a.photoCount} PHOTOS
                </Typography>
              </Box>
              <ChipRemove
                type='button'
                aria-label={`Unlink ${a.title}`}
                onClick={() => {
                  onRemoveSourceAlbum(a.id)
                  if (filterAlbumId === a.id) setFilterAlbumId('all')
                }}
              >
                ✕
              </ChipRemove>
            </AlbumChip>
          ))}

          {/* Multi-album linking deferred to phase 2 — PRO gate stays wired
              in `openPicker` below for whenever it comes back, but the
              affordance itself is hidden (not shown-then-blocked) once one
              album is linked, same "hide, don't gate-and-tease" pattern
              used for Hall/Spaces and the Studio room-mode toggle. Anyone
              who already linked 2+ albums before this change keeps full
              use of them (filter chips below, unlink) — this only stops
              adding more. */}
          {linkedAlbums.length === 0 && (
            <LinkAlbumLink type='button' onClick={openPicker}>
              + Link album
            </LinkAlbumLink>
          )}

          {linkedAlbums.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, px: '2px', mb: '6px' }}>
              <SectionAction
                onClick={() => setFilterAlbumId('all')}
                style={{ opacity: filterAlbumId === 'all' ? 1 : 0.55, color: filterAlbumId === 'all' ? STUDIO_ACCENT.violet : undefined }}
              >
                ALL
              </SectionAction>
              {linkedAlbums.map((a) => (
                <SectionAction
                  key={a.id}
                  onClick={() => setFilterAlbumId(a.id)}
                  style={{
                    opacity: filterAlbumId === a.id ? 1 : 0.55,
                    color: filterAlbumId === a.id ? STUDIO_ACCENT.violet : undefined,
                    maxWidth: 72,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.title.slice(0, 10).toUpperCase()}
                </SectionAction>
              ))}
            </Box>
          )}

          <AssetsHint>
            {!hasRealPhotos
              ? 'Link an album, then use + Upload in the grid — or hang prints from the wall.'
              : assetsFromWallOnly
                ? 'Synced from wall prints until an album is linked.'
                : selectedId
                  ? 'A print is selected — tap a photo, or + Upload, to drop it in that spot.'
                  : 'Tap + Upload to add photos, or select a print and tap a photo to swap.'}
          </AssetsHint>

          <AssetGrid>
            {onUploadPhotos && (
              <UploadTile
                type='button'
                onClick={openUpload}
                disabled={!!uploadingPhotos || linkedAlbums.length === 0}
                title={
                  linkedAlbums.length === 0
                    ? 'Link an album first'
                    : uploadingPhotos
                      ? 'Uploading…'
                      : selectedId
                        ? `Upload into ${uploadTargetTitle} — drops into the selected slot`
                        : `Upload photos into ${uploadTargetTitle}`
                }
                aria-label={
                  linkedAlbums.length === 0 ? 'Link an album to upload' : `Upload photos into ${uploadTargetTitle}`
                }
              >
                <UploadTilePlus aria-hidden>{uploadingPhotos ? '…' : '+'}</UploadTilePlus>
                <UploadTileLabel>
                  {uploadingPhotos
                    ? uploadProgress != null
                      ? `${uploadProgress}%`
                      : 'Uploading'
                    : 'Upload'}
                </UploadTileLabel>
              </UploadTile>
            )}
            {assetsLoading && <Box sx={{ fontSize: 11, color: 'var(--ds-night-ink-subtle)', gridColumn: '1 / -1' }}>Loading…</Box>}
            {visiblePhotos.map((photo) => (
              <AssetTile key={`${photo.albumId || 'wall'}-${photo.id}`} onClick={() => onPickAsset(photo)}>
                <img src={photo.thumbUrl || photo.url} alt='' />
              </AssetTile>
            ))}
          </AssetGrid>
        </Scroll>
      )}
    </Root>
  )
}
